// /client/src/components/V3LeadsJobProgress.js
//
// Phase 7: "what happened in this enrichment run?" panel. Polls
// GET /api/batchdata/v3/leads/jobs/:jobId at a conservative interval, stops
// polling on unmount or once the job reaches a terminal state, and exposes
// cancel / safe-retry / uncertain-retry actions. Never shows raw API
// responses — only the fields the backend already summarizes.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-hot-toast';
import { scrapingService } from '../services/scrapingService';
import { isTerminalWorkflow } from '../utils/v3LeadsHelpers';
import V3PriorUncertainWarning from './V3PriorUncertainWarning';
import V3UncertainRecoveryPanel from './V3UncertainRecoveryPanel';

const POLL_INTERVAL_MS = 4000;

function StatBox({ label, value, color = 'text-gray-900' }) {
  return (
    <div className="bg-gray-50 rounded-lg border p-3 text-center">
      <div className={`text-xl font-bold ${color}`}>{value}</div>
      <div className="text-xs text-gray-500 mt-0.5">{label}</div>
    </div>
  );
}

const WORKFLOW_LABELS = {
  preparing: 'Preparing enrichment', enriching: 'Enriching / verifying V3 items',
  checking: 'Checking remaining effective emails', verifying: 'Verifying remaining emails',
  waiting: 'Waiting for existing verification work', complete: 'Complete',
  unresolved: 'Complete with unresolved work ? review required', cancelled: 'Cancelled',
};

const STATUS_LABELS = {
  queued: 'Queued',
  running: 'Running',
  cancelling: 'Cancelling…',
  completed: 'Completed',
  completed_with_errors: 'Completed (with some errors)',
  cancelled: 'Cancelled',
  failed: 'Failed',
};

const V3LeadsJobProgress = ({ jobId, onJobCleared, onResultsAvailable, onStatus, resultsStatus, recoveryScope, onRecoveryResultsAvailable }) => {
  const [job, setJob] = useState(null);
  const [error, setError] = useState(null);
  const [busyAction, setBusyAction] = useState(null); // 'cancel' | 'retry' | 'uncertain-retry' | null
  const [confirmUncertain, setConfirmUncertain] = useState(false);
  const timerRef = useRef(null);
  const mountedRef = useRef(true);
  const callbacksRef = useRef({ onJobCleared, onResultsAvailable, onStatus });
  callbacksRef.current = { onJobCleared, onResultsAvailable, onStatus };
  const currentJobId = useRef(jobId);
  currentJobId.current = jobId;
  const notifiedTerminalRef = useRef(null);
  const pollGeneration = useRef(0);

  const poll = useCallback(async function pollJob() {
    if (!jobId) return;
    clearTimeout(timerRef.current);
    const generation = ++pollGeneration.current;
    try {
      const res = await scrapingService.getV3LeadsJob(jobId);
      if (!mountedRef.current || currentJobId.current !== jobId || generation !== pollGeneration.current) return;
      setJob(res);
      callbacksRef.current.onStatus?.(res);
      setError(null);
      if (isTerminalWorkflow(res) && notifiedTerminalRef.current !== jobId) {
        notifiedTerminalRef.current = jobId;
        if (callbacksRef.current.onResultsAvailable) callbacksRef.current.onResultsAvailable(res);
      }
      if (!isTerminalWorkflow(res)) notifiedTerminalRef.current = null;
      if (!isTerminalWorkflow(res)) timerRef.current = setTimeout(pollJob, POLL_INTERVAL_MS);
      return res;
    } catch (err) {
      if (!mountedRef.current || currentJobId.current !== jobId || generation !== pollGeneration.current) return;
      if (err.status === 404) {
        // Stale job id — clear it gracefully, never auto-create a new job.
        if (callbacksRef.current.onJobCleared) callbacksRef.current.onJobCleared();
        return;
      }
      // Temporary polling failure: keep showing the last-known state, just surface the error.
      setError(err.message);
      timerRef.current = setTimeout(pollJob, POLL_INTERVAL_MS);
    }
  }, [jobId]);

  useEffect(() => {
    mountedRef.current = true;
    poll();
    return () => { mountedRef.current = false; clearTimeout(timerRef.current); };
  }, [poll]);

  if (!jobId) return null;

  const handleCancel = async () => {
    setBusyAction('cancel');
    try {
      await scrapingService.cancelV3LeadsJob(jobId);
      toast.success('Cancellation requested. Already-in-flight paid responses will still be safely persisted — spent credits cannot be undone.');
      await poll();
    } catch (err) {
      toast.error(err.message || 'Failed to cancel job');
    } finally {
      setBusyAction(null);
    }
  };

  const handleRetrySafe = async () => {
    setBusyAction('retry');
    try {
      const res = await scrapingService.retryV3LeadsJobFailures(jobId);
      toast.success(`Requeued ${res.requeuedCount ?? 0} safe failure(s)`);
      await poll();
    } catch (err) {
      toast.error(err.message || 'Failed to retry safe failures');
    } finally {
      setBusyAction(null);
    }
  };

  const handleRetryEmails = async () => {
    if (!confirmUncertain) return;
    setBusyAction('emails');
    try {
      await scrapingService.retryV3UnresolvedEmails(jobId, true);
      setConfirmUncertain(false);
      notifiedTerminalRef.current = null;
      timerRef.current = null;
      await poll();
    } catch (err) { toast.error(err.message); }
    finally { setBusyAction(null); }
  };

  if (!job) {
    return (
      <div className="bg-white rounded-xl border shadow-sm p-4 text-sm text-gray-500 animate-pulse">
        {error ? `${error}. Retrying status...` : 'Loading job status…'}
      </div>
    );
  }

  const terminal = isTerminalWorkflow(job);
  const canCancel = !terminal && (job.verifyRemainingEffectiveEmails || job.status === 'queued' || job.status === 'running');
  const hasSafeFailures = (job.failedItems ?? 0) > 0;
  const verificationOnly = job.targetCounts?.zerobounceOnlyTargets != null
    || job.targetCounts?.verificationMode === 'effective_pending_emails';
  const emailCounts = job.targetCounts || {};
  const priorUncertainAttempts = emailCounts.priorUncertainAttempts ?? 0;
  const countyNames = emailCounts.countyNames || [];
  const countiesLabel = countyNames.length === 1
    ? (/county$/i.test(countyNames[0]) ? countyNames[0] : `${countyNames[0]} County`)
    : countyNames.map((name) => name.replace(/\s+county$/i, '')).join(', ');
  const scopeLabel = `${job.filterMode === 'atmax' ? 0 : (job.minAcres ?? 0)} - ${job.filterMode === 'atleast' ? 'unlimited' : (job.maxAcres ?? 'unlimited')} acres`;
  const uncertainRecovery = <V3UncertainRecoveryPanel currentJobId={jobId}
    scope={job.countyIds?.length ? { countyIds: job.countyIds, filterMode: job.filterMode,
      minAcres: job.minAcres, maxAcres: job.maxAcres } : recoveryScope}
    onChanged={poll} onResultsAvailable={onRecoveryResultsAvailable || onResultsAvailable} />;

  if (job.targetCounts?.endToEnd) {
    const progress = job.progress;
    const stage = resultsStatus === 'loading' ? 'Loading leads results' : progress?.stage || 'Preparing scope';
    return <div className="bg-white rounded-xl border shadow-sm p-4 sm:p-6" aria-label="Enrichment progress">
      <h3 className="font-semibold">{resultsStatus === 'complete' ? 'Ready for filtering/export' : stage}</h3>
      <p className="text-sm mt-1">{countiesLabel} · {scopeLabel}</p>
      {job.workflow?.noPaidWork && !progress?.evidenceMissing && !progress?.batchDataUnresolved && !priorUncertainAttempts && <p className="text-sm text-teal-800">The selected scope was already complete. No paid work was needed.</p>}
      <V3PriorUncertainWarning count={priorUncertainAttempts} />
      {job.paidDispatchPaused && <p role="alert" className="mt-3 text-amber-900">BatchData paid dispatch is paused. Queued targets remain available. Review interrupted attempts and resume dispatch explicitly after the runtime issue is resolved.</p>}
      {uncertainRecovery}
      <p className="text-sm mt-3">{progress?.completed ?? 0} / {progress?.total ?? 0} owner and email tasks complete ({progress?.percent ?? 0}%)</p>
      <progress className="w-full" aria-label="Overall processing progress" max="100" value={progress?.percent ?? 0} />
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3" aria-label="Owner processing status">
        <StatBox label="Queued" value={job.queuedItems ?? 0} />
        <StatBox label="Running" value={job.runningItems ?? 0} color="text-blue-600" />
        <StatBox label="Failed" value={job.failedItems ?? 0} color="text-red-600" />
        <StatBox label="Needs confirmation" value={job.retryConfirmationRequiredCount ?? 0} color="text-amber-600" />
      </div>
      {error && <p role="alert" className="text-amber-800">{error}</p>}
      <div className="overflow-x-auto mt-3"><table className="w-full text-sm text-left">
        <thead><tr>{['County', 'Owners', 'BatchData', 'Emails', 'Unresolved', 'Reusable', 'Skipped owners', 'Held for confirmation', 'State'].map((label) => <th key={label} className="p-2">{label}</th>)}</tr></thead>
        <tbody>{(progress?.counties || []).map((county) => <tr key={county.countyId} className="border-t">
          <td className="p-2">{county.countyName}</td><td>{county.ownersInScope}</td>
          <td>{county.batchDataCompleted} / {county.batchDataTarget}</td><td>{county.emailCompleted} / {county.emailTarget}</td>
          <td>{county.unresolved}</td><td>{county.reused}</td><td>{county.skipped}</td>
          <td>{emailCounts.priorUncertainAttemptsByCounty?.[county.countyId] ?? 0}</td><td>{county.state}</td>
        </tr>)}</tbody>
      </table></div>
      {!!progress?.unresolved && <p className="text-amber-800 mt-3">{progress.unresolved} emails remain unresolved. Available leads can still be filtered and exported.</p>}
      {!!progress?.evidenceMissing && <p className="text-amber-800">{progress.evidenceMissing} owners need evidence review.</p>}
      {!!progress?.batchDataUnresolved && <p className="text-amber-800">{progress.batchDataUnresolved} owners need enrichment review, including missing addresses.</p>}
      {job.workflow?.stage === 'unresolved' && job.workflow?.child?.zerobouncePendingCount > 0 && <div className="mt-3">
        <label className="block text-sm"><input type="checkbox" checked={confirmUncertain} onChange={(event) => setConfirmUncertain(event.target.checked)} /> Retry unresolved verification; an earlier uncertain request may have used credits.</label>
        <button type="button" disabled={!confirmUncertain || !!busyAction} onClick={handleRetryEmails} className="mt-2 px-4 py-2 border rounded disabled:opacity-50">Retry unresolved emails</button>
      </div>}
      {canCancel && <button type="button" disabled={!!busyAction} onClick={handleCancel} className="mt-3 px-4 py-2 border rounded">Cancel processing</button>}
      {terminal && <button type="button" onClick={onJobCleared} className="mt-3 ml-3 underline text-sm">Dismiss run</button>}
    </div>;
  }

  return (
    <div className="bg-white rounded-xl border shadow-sm p-4 sm:p-6">
      <div className="flex justify-between items-center mb-3">
        <h3 className="text-base font-semibold text-gray-900">
          {verificationOnly ? 'Pending Email Verification' : 'V3 Enrichment Job'} — <span className="font-mono text-sm">{job.jobId}</span>
        </h3>
        <span className={`px-2 py-1 rounded text-xs font-medium ${
          terminal
            ? (job.workflow?.stage === 'unresolved' || job.status === 'failed' || job.status === 'completed_with_errors' ? 'bg-orange-100 text-orange-800' : 'bg-green-100 text-green-800')
            : 'bg-blue-100 text-blue-800'
        }`}>
          {job.preparationState === 'preparing' ? WORKFLOW_LABELS.preparing : (job.verifyRemainingEffectiveEmails
            ? WORKFLOW_LABELS[job.workflow?.stage] || 'Checking workflow status' : STATUS_LABELS[job.status] || job.status)}
        </span>
      </div>

      <div className="mb-4 text-sm text-gray-700">
        <p>{countiesLabel || `County IDs: ${(job.countyIds || []).join(', ') || 'unavailable for this older job'}`}</p>
        <p>{scopeLabel}</p>
      </div>

      <V3PriorUncertainWarning count={priorUncertainAttempts} />
      {job.paidDispatchPaused && <p role="alert" className="mb-3 text-amber-900">BatchData paid dispatch is paused. Queued targets remain available. Review interrupted attempts and resume dispatch explicitly after the runtime issue is resolved.</p>}

      {job.verifyRemainingEffectiveEmails && <div className="mb-4 p-3 border rounded text-sm">
        <p>Parent job: {job.jobId} ? Individual job: {STATUS_LABELS[job.status] || job.status}</p>
        <p>Expected owner items: {job.expectedItemCount ?? 'Unavailable'} ? Stored owner items: {job.totalItems ?? 0}</p>
        <p>Scope verification child: {job.workflow?.child?.jobId || job.workflow?.childJobId || 'Not needed or not scheduled yet'}</p>
        {job.workflow?.child && <p>Child owner items: {job.workflow.child.totalItems ?? 0} ? Completed: {job.workflow.child.completedItems ?? 0}
          {' ? '}Pending: {job.workflow.child.zerobouncePendingCount ?? 0} ? Review/errors: {job.workflow.child.verificationPendingErrorCount ?? 0}</p>}
        {!!job.workflow?.waitingJobIds?.length && <p>Waiting for verification jobs: {job.workflow.waitingJobIds.join(', ')}</p>}
        <p>Original email estimates: {job.targetCounts?.pendingEmailEstimates?.pendingEmailRows ?? 'Unavailable'} pending rows;
          {' '}{job.targetCounts?.pendingEmailEstimates?.existingVerificationReuseCount ?? 'Unavailable'} reusable results;
          {' '}{job.targetCounts?.pendingEmailEstimates?.newExternalVerificationCount ?? 'Unavailable'} new calls.</p>
        <p>Remaining scope: {job.workflow?.pendingEmailRows ?? 'Not checked yet'} email rows;
          {' '}{job.workflow?.newExternalVerificationCount ?? 'Not checked yet'} estimated new calls.</p>
        {job.workflow?.reason && <p>Review reason: {job.workflow.reason.replace(/_/g, ' ')}</p>}
      </div>}

      {error && <div className="mb-3 text-xs text-amber-700">⚠ {error} (showing last known status)</div>}
      {job.verificationPendingErrorCount > 0 && <p className="mb-3 text-sm text-amber-800">
        {job.verificationPendingErrorCount} owner(s) have verification errors. Their emails remain pending for retry.
      </p>}

      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3 mb-4">
        <StatBox label={verificationOnly ? 'Owners to process' : 'Total'} value={job.totalItems ?? 0} />
        {!verificationOnly && <StatBox label="Queued" value={job.queuedItems ?? 0} />}
        <StatBox label={verificationOnly ? 'Owners processing' : 'Running'}
          value={(verificationOnly ? job.verificationProcessingCount : job.runningItems) ?? 0} color="text-blue-600" />
        <StatBox label={verificationOnly ? 'Owners completed' : 'Completed'} value={job.completedItems ?? 0} color="text-green-600" />
        <StatBox label={verificationOnly ? 'Owners failed' : 'Failed'} value={job.failedItems ?? 0} color="text-red-600" />
        {!verificationOnly && <StatBox label="Needs confirmation" value={job.retryConfirmationRequiredCount ?? 0} color="text-amber-600" />}
        <StatBox label={verificationOnly ? 'Owners awaiting verification' : 'ZeroBounce pending'}
          value={Math.max(0, (job.zerobouncePendingCount ?? 0) - (verificationOnly ? (job.verificationProcessingCount ?? 0) : 0))} color="text-purple-600" />
      </div>

      {verificationOnly && <div className="mb-4">
        <div role="progressbar" aria-label="Owners completed" aria-valuemin={0} aria-valuemax={job.totalItems || 1}
          aria-valuenow={job.completedItems || 0} className="h-2 bg-blue-100 rounded overflow-hidden mb-3">
          <div className="h-full bg-blue-600" style={{ width: `${Math.min(100, 100 * (job.completedItems || 0) / (job.totalItems || 1))}%` }} />
        </div>
        <p className="text-xs text-gray-500 mb-2">One job item per owner. Email estimates below describe the scope at creation.</p>
        <dl className="grid grid-cols-2 gap-2 text-sm">
          <dt>Pending email rows</dt><dd>{emailCounts.pendingEmailRows ?? 'Unavailable'}</dd>
          <dt>Unique canonical pending emails</dt><dd>{emailCounts.uniqueCanonicalPendingEmails ?? 'Unavailable'}</dd>
          <dt>Existing verifications reused (estimated)</dt><dd>{emailCounts.existingVerificationReuseCount ?? 'Unavailable'}</dd>
          <dt>New ZeroBounce calls required (estimated)</dt><dd>{emailCounts.newExternalVerificationCount ?? 'Unavailable'}</dd>
          <dt>BatchData calls</dt><dd>0</dd>
        </dl>
      </div>}

      <div className="text-xs text-gray-500 mb-4 flex flex-wrap gap-x-4 gap-y-1">
        <span>{verificationOnly ? 'BatchData credits' : 'Credits used'}: <strong>{job.creditsUsed ?? 0}</strong></span>
        {!verificationOnly && job.estimatedCreditsMin != null && (
          <span>Estimated range: {job.estimatedCreditsMin}–{job.estimatedCreditsMax}</span>
        )}
        {job.createdAt && <span>Created: {new Date(job.createdAt).toLocaleString()}</span>}
        {job.startedAt && <span>Started: {new Date(job.startedAt).toLocaleString()}</span>}
        {job.finishedAt && <span>Finished: {new Date(job.finishedAt).toLocaleString()}</span>}
      </div>

      {job.status === 'completed_with_errors' && (
        <div className="mb-3 p-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-800">
          Some owners succeeded and are already available in Leads results below; others failed — see counts above.
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3">
        {canCancel && (
          <button
            onClick={handleCancel}
            disabled={busyAction === 'cancel'}
            className="px-3 py-1.5 rounded-md text-sm font-medium border border-red-300 text-red-700 hover:bg-red-50 disabled:opacity-50"
          >
            {busyAction === 'cancel' ? 'Cancelling…' : 'Cancel'}
          </button>
        )}
        {hasSafeFailures && (
          <button
            onClick={handleRetrySafe}
            disabled={busyAction === 'retry'}
            className="px-3 py-1.5 rounded-md text-sm font-medium border border-blue-300 text-blue-700 hover:bg-blue-50 disabled:opacity-50"
          >
            {busyAction === 'retry' ? 'Retrying…' : 'Retry Safe Failures'}
          </button>
        )}
        {terminal && (
          <button
            onClick={() => onJobCleared && onJobCleared()}
            className="px-3 py-1.5 rounded-md text-sm font-medium border bg-white text-gray-700 hover:bg-gray-50"
          >
            Dismiss
          </button>
        )}
      </div>

      {uncertainRecovery}
    </div>
  );
};

export default V3LeadsJobProgress;
